import {createRouter, RouterProvider} from '@tanstack/react-router';
import ErrorView from '@/shared/components/ErrorBoundary/ErrorView';
import useLoggedUserContext, {
  useCompany,
  useIsLoggedIn,
  useIsPatient,
  useIsPractitioner
} from '@/shared/contexts/UserContext/useUserContext';
import {routeTree} from './routeTree.gen';
import type {AppRouterContext} from './types';

function useSafeCompany() {
  try {
    return useCompany();
  } catch {
    return null;
  }
}

function useSafeUser() {
  try {
    return useLoggedUserContext();
  } catch {
    return null;
  }
}

function useSafeIsPractitioner() {
  try {
    return useIsPractitioner();
  } catch {
    return false;
  }
}

function useSafeIsPatient() {
  try {
    return useIsPatient();
  } catch {
    return false;
  }
}

const router = createRouter({
  routeTree,
  context: {
    currentUser: null,
    isLoggedIn: false,
    isPractitioner: false,
    isPatient: false,
    company: null
  } satisfies AppRouterContext,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  basepath: '/',
  defaultErrorComponent: () => ErrorView,
  defaultPreloadDelay: 0,
  notFoundMode: 'root'
});

export default function AppRouterProvider() {
  const isLoggedIn = useIsLoggedIn();
  const company = useSafeCompany();
  const safeUser = useSafeUser();
  const user = safeUser ? safeUser.user : null;
  const isPractitioner = useSafeIsPractitioner();
  const isPatient = useSafeIsPatient();

  return (
    <RouterProvider
      router={router}
      context={{
        currentUser: user,
        isLoggedIn,
        isPractitioner,
        isPatient,
        company
      }}
    />
  );
}
